import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  IonIcon,
  IonRefresher,
  IonRefresherContent,
  IonSkeletonText,
  RefresherEventDetail,
} from '@ionic/react';
import ProPageModal from '../../components/pro/bottom-sheet/ProPageModal';
import { ProConfirmSheet } from '../../components/pro/primitives';
import ProSectionHeader from '../../components/pro/ProSectionHeader';
import ProCard from '../../components/pro/ProCard';
import ProListRow from '../../components/pro/ProListRow';
import {
  personOutline,
  lockClosedOutline,
  notificationsOutline,
  logOutOutline,
  businessOutline,
  mailUnreadOutline,
  trashOutline,
  warningOutline,
} from 'ionicons/icons';
import { useQuery } from '@tanstack/react-query';
import api from '../../lib/api';
import { useOrganizations } from '../../hooks/useOrganizations';
import { usePendingToast } from './useProToast';
import { proSignOut } from './proSignOut';
import './ProAccount.css';

interface ProfileData {
  firstName?: string;
  lastName?: string;
  first_name?: string;
  last_name?: string;
  displayName?: string;
  display_name?: string;
  email?: string;
  emailVerified?: boolean;
  email_verified?: boolean;
  role?: string;
}

const getDisplayName = (profile?: ProfileData): string => {
  if (!profile) return '';
  return (
    profile.displayName ||
    profile.display_name ||
    `${profile.firstName || profile.first_name || ''} ${profile.lastName || profile.last_name || ''}`.trim()
  );
};

const ProProfilePage: React.FC = () => {
  const { t } = useTranslation('pro');
  const navigate = useNavigate();
  const { showSuccess, showError } = usePendingToast();
  const [showSignOutConfirm, setShowSignOutConfirm] = useState(false);
  const [showDeleteIntent, setShowDeleteIntent] = useState(false);
  const [sendingVerify, setSendingVerify] = useState(false);

  const { data: profile, isLoading, refetch } = useQuery<ProfileData>(
    ['pro-profile'],
    async () => {
      const { data } = await api.get('/users/profile');
      return data.data || data;
    }
  );

  const { organizations, loading: orgsLoading } = useOrganizations();

  const handleRefresh = async (e: CustomEvent<RefresherEventDetail>) => {
    await refetch();
    e.detail.complete();
  };

  const handleSignOut = () => proSignOut();

  const handleSendVerification = async () => {
    setSendingVerify(true);
    try {
      await api.post('/auth/send-verification');
      showSuccess('Verification email sent. Check your inbox.');
    } catch (err: any) {
      showError(err?.response?.data?.error || 'Could not send verification email.');
    } finally {
      setSendingVerify(false);
    }
  };

  const emailVerified = profile?.emailVerified ?? profile?.email_verified ?? true;
  const showVerifyBanner = !isLoading && profile && !emailVerified;

  return (
    <>
      <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
        <IonRefresherContent />
      </IonRefresher>

      <div className="pb-8">
        <div className="px-4 pt-4 pb-2">
          <p className="text-base font-semibold text-text-primary dark:text-text-dark-primary">
            {isLoading ? (
              <IonSkeletonText animated style={{ width: '160px', height: '20px' }} />
            ) : (
              getDisplayName(profile) || 'Your account'
            )}
          </p>
          <p className="mt-0.5 text-sm text-text-secondary dark:text-text-dark-secondary">
            {isLoading ? (
              <IonSkeletonText animated style={{ width: '200px', height: '16px', marginTop: '4px' }} />
            ) : (
              profile?.email || '—'
            )}
          </p>
        </div>

        {showVerifyBanner && (
          <div className="px-2 pb-2">
            <div className="pro-account__verify-banner" role="status">
              <IonIcon icon={mailUnreadOutline} />
              <span>Your email isn&apos;t verified yet.</span>
              <button
                type="button"
                className="pro-account__verify-banner-btn"
                onClick={handleSendVerification}
                disabled={sendingVerify}
              >
                {sendingVerify ? 'Sending…' : 'Verify'}
              </button>
            </div>
          </div>
        )}

        <ProSectionHeader title="Account" icon={personOutline} />
        <div className="px-2">
          <ProCard>
            <ProListRow
              icon={personOutline}
              title="Personal details"
              note="Name, contact, address, bio"
              onClick={() => navigate('/pro/profile/edit')}
            />
            <ProListRow
              icon={lockClosedOutline}
              title="Password"
              note="Change your sign-in password"
              onClick={() => navigate('/pro/profile/security')}
            />
            <ProListRow
              icon={notificationsOutline}
              title="Notifications"
              note="Alerts and email digest"
              onClick={() => navigate('/pro/profile/notifications')}
            />
          </ProCard>
        </div>

        <ProSectionHeader title="Organizations" icon={businessOutline} />
        <div className="px-2">
          <ProCard>
            {orgsLoading ? (
              <ProListRow
                title={<IonSkeletonText animated style={{ width: '60%', height: '16px' }} />}
              />
            ) : organizations.length > 0 ? (
              organizations.map((org) => (
                <ProListRow
                  key={org.id}
                  icon={businessOutline}
                  title={org.name}
                  note={
                    org.role ? <span className="capitalize">{org.role}</span> : undefined
                  }
                />
              ))
            ) : (
              <ProListRow title="No organizations" />
            )}
          </ProCard>
        </div>

        <ProSectionHeader title="Danger zone" icon={warningOutline} iconColor="danger" />
        <div className="px-2">
          <ProCard>
            <ProListRow
              icon={trashOutline}
              title="Delete account"
              note="Permanently delete your account and all associated data"
              tone="danger"
              onClick={() => setShowDeleteIntent(true)}
            />
          </ProCard>
        </div>

        <div className="px-4 mt-6">
          <button
            type="button"
            className="pro-account__signout"
            onClick={() => setShowSignOutConfirm(true)}
          >
            <IonIcon icon={logOutOutline} className="text-lg" />
            Sign out
          </button>
        </div>
      </div>

      <ProConfirmSheet
        isOpen={showSignOutConfirm}
        onDismiss={() => setShowSignOutConfirm(false)}
        title={t('common.signOutConfirmTitle')}
        body={t('common.signOutConfirmBody')}
        confirmLabel={t('nav.signOut')}
        cancelLabel={t('common.cancel')}
        onConfirm={handleSignOut}
        variant="danger"
      />

      <ProPageModal
        isOpen={showDeleteIntent}
        onDismiss={() => setShowDeleteIntent(false)}
        title="Delete account?"
      >
        <div className="px-4 py-5 space-y-4">
          <p className="text-sm text-text-secondary dark:text-text-dark-secondary leading-relaxed">
            This permanently deletes your account and all associated data, and
            signs you out immediately. The next screen shows exactly what gets
            removed before you confirm.
          </p>
          <div className="space-y-2">
            <button
              type="button"
              onClick={() => {
                setShowDeleteIntent(false);
                navigate('/pro/profile/delete');
              }}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-red-600 text-white font-semibold text-sm transition-colors active:bg-red-700"
            >
              <IonIcon icon={trashOutline} className="text-lg" />
              Continue
            </button>
            <button
              type="button"
              onClick={() => setShowDeleteIntent(false)}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-transparent text-text-secondary dark:text-text-dark-secondary font-semibold text-sm transition-colors active:bg-black/5 dark:active:bg-white/5"
            >
              Cancel
            </button>
          </div>
        </div>
      </ProPageModal>
    </>
  );
};

export default ProProfilePage;
